import * as React from 'react';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardMedia,
  Grid,
  IconButton,
  Typography,
  TablePagination,
  Checkbox,
  Modal,
  TextField,
  Stack,
} from '@mui/material';
import { useNavigate } from 'react-router';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';

const API_URL = import.meta.env.VITE_API_URL;
const IMAGE_URL = import.meta.env.VITE_IMAGE_URL;

const COLORS = [
  '#f87171',
  '#fb923c',
  '#facc15',
  '#a3e635',
  '#4ade80',
  '#34d399',
  '#2dd4bf',
  '#22d3ee',
  '#38bdf8',
  '#60a5fa',
  '#818cf8',
  '#a78bfa',
  '#c084fc',
  '#e879f9',
  '#f472b6',
  '#fb7185',
  '#ef4444',
  '#10b981',
  '#3b82f6',
  '#8b5cf6',
  '#ec4899',
  '#eab308',
  '#14b8a6',
  '#6366f1',
  '#22c55e',
];

export default function MediaList() {
  const navigate = useNavigate();

  const [media, setMedia] = React.useState<any[]>([]);
  const [total, setTotal] = React.useState(0);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(12);

  const [selected, setSelected] = React.useState<Set<number>>(new Set());

  const [search, setSearch] = React.useState('');
  const [selectedTags, setSelectedTags] = React.useState<Set<number>>(
    new Set(),
  );
  const [allTags, setAllTags] = React.useState<any[]>([]);

  const [tagsOpen, setTagsOpen] = React.useState(false);
  const [newTagName, setNewTagName] = React.useState('');
  const [newTagColor, setNewTagColor] = React.useState('#f87171');

  const [editingTagId, setEditingTagId] = React.useState<number | null>(null);
  const [editingName, setEditingName] = React.useState('');

  const fileInputRef = React.useRef<HTMLInputElement | null>(null);

  const loadTags = () => {
    fetch(`${API_URL}/tags`)
      .then((r) => r.json())
      .then((data) => setAllTags(data.data));
  };

  const fetchMedia = () => {
    const params = new URLSearchParams();

    params.append('page', String(page + 1));
    params.append('perPage', String(rowsPerPage));

    if (search) params.append('search', search);
    if (selectedTags.size > 0) {
      selectedTags.forEach((id) => params.append('tagIds', String(id)));
    }

    fetch(`${API_URL}/media?${params.toString()}`)
      .then((r) => r.json())
      .then((data) => {
        setMedia(data.data);
        setTotal(data.total);
        setSelected(new Set());
      });
  };

  React.useEffect(() => {
    fetchMedia();
  }, [page, rowsPerPage, search, selectedTags]);

  React.useEffect(() => {
    loadTags();
  }, []);

  const handleDelete = (id: number) => {
    if (!confirm('Видалити цю медіа?')) return;
    fetch(`${API_URL}/media/${id}`, { method: 'DELETE' }).then(fetchMedia);
  };

  const handleMultiDelete = () => {
    if (selected.size === 0) return;
    if (!confirm(`Видалити ${selected.size} медіа?`)) return;

    Promise.all(
      Array.from(selected).map((id) =>
        fetch(`${API_URL}/media/${id}`, { method: 'DELETE' }),
      ),
    ).then(fetchMedia);
  };

  const handleSelectAll = () => {
    const allIds = media.map((m) => m.mediaId);
    const next = new Set(selected);

    if (selected.size === media.length) next.clear();
    else allIds.forEach((id) => next.add(id));

    setSelected(next);
  };

  const handleToggle = (id: number) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };

  const toggleTag = (id: number) => {
    const next = new Set(selectedTags);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelectedTags(next);
    setPage(0);
  };

  const handleMultiUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const arr = Array.from(files);
    const BATCH_SIZE = 10;

    for (let i = 0; i < arr.length; i += BATCH_SIZE) {
      const batch = arr.slice(i, i + BATCH_SIZE);
      const formData = new FormData();
      batch.forEach((f) => formData.append('files', f));

      await fetch(`${API_URL}/media/multi`, {
        method: 'POST',
        body: formData,
      });
    }

    e.target.value = '';
    fetchMedia();
  };

  return (
    <Box sx={{ mt: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h5">Медіа бібліотека</Typography>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="contained" onClick={() => navigate('/media/new')}>
            Додати медіа
          </Button>

          <Button
            variant="outlined"
            onClick={() => fileInputRef.current?.click()}
          >
            Додати мультімедія
          </Button>

          <Button variant="outlined" onClick={() => setTagsOpen(true)}>
            Теги
          </Button>

          <input
            hidden
            ref={fileInputRef}
            type="file"
            multiple
            onChange={handleMultiUpload}
          />
        </Box>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <Button variant="outlined" onClick={handleSelectAll}>
            {selected.size === media.length ? 'Відмінити' : 'Обрати все'}
          </Button>

          <Button
            variant="outlined"
            color="error"
            onClick={handleMultiDelete}
            disabled={selected.size === 0}
          >
            Видалити вибране ({selected.size})
          </Button>
        </Box>

        <TextField
          fullWidth
          size="small"
          placeholder="Пошук..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(0);
          }}
        />

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
          {allTags.map((t) => {
            const active = selectedTags.has(t.tagId);

            return (
              <Box
                key={t.tagId}
                onClick={() => toggleTag(t.tagId)}
                sx={{
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 2,
                  bgcolor: t.color,
                  opacity: active ? 1 : 0.25,
                  cursor: 'pointer',
                }}
              >
                <Typography variant="caption">{t.name}</Typography>
              </Box>
            );
          })}
        </Box>
      </Box>

      <Grid container spacing={3}>
        {media.map((item) => (
          <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={item.mediaId}>
            <Card
              sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}
            >
              <Box sx={{ position: 'relative' }}>
                <Checkbox
                  checked={selected.has(item.mediaId)}
                  onChange={() => handleToggle(item.mediaId)}
                  sx={{ position: 'absolute', top: 8, left: 8 }}
                />

                <CardMedia
                  component="img"
                  height="220"
                  image={`${IMAGE_URL}${item.imageUrl}`}
                />
              </Box>

              <CardContent sx={{ flexGrow: 1 }}>
                <Typography variant="body2">
                  {item.title || 'Без назви'}
                </Typography>

                <Box
                  sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}
                >
                  {item.Tags?.map((t: any) => (
                    <Box
                      key={t.tagId}
                      sx={{
                        px: 1,
                        py: 0.3,
                        borderRadius: 2,
                        bgcolor: t.color,
                      }}
                    >
                      <Typography variant="caption">{t.name}</Typography>
                    </Box>
                  ))}
                </Box>
              </CardContent>

              <CardActions sx={{ justifyContent: 'flex-end' }}>
                <IconButton onClick={() => navigate(`/media/${item.mediaId}`)}>
                  <EditIcon />
                </IconButton>

                <IconButton onClick={() => handleDelete(item.mediaId)}>
                  <DeleteIcon />
                </IconButton>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={(e, p) => setPage(p)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
      />

      <Modal open={tagsOpen} onClose={() => setTagsOpen(false)}>
        <Box sx={{ p: 3, bgcolor: '#fff', width: 500, mx: 'auto', mt: '10%' }}>
          <Typography variant="h6">Теги</Typography>

          <Stack direction="row" gap={2} mt={2} alignItems="center">
            <TextField
              placeholder="Новий тег..."
              value={newTagName}
              onChange={(e) => setNewTagName(e.target.value)}
              fullWidth
              size="small"
            />

            <Box
              sx={{
                display: 'flex',
                gap: 0.5,
                flexWrap: 'wrap',
                maxWidth: 160,
              }}
            >
              {COLORS.map((c) => (
                <Box
                  key={c}
                  onClick={() => setNewTagColor(c)}
                  sx={{
                    width: 16,
                    height: 16,
                    borderRadius: '50%',
                    bgcolor: c,
                    cursor: 'pointer',
                    border:
                      newTagColor === c ? '2px solid #000' : '1px solid #ddd',
                  }}
                />
              ))}
            </Box>

            <Button
              variant="contained"
              color="secondary"
              onClick={async () => {
                if (!newTagName) return;

                await fetch(`${API_URL}/tags`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    name: newTagName,
                    color: newTagColor,
                  }),
                });

                setNewTagName('');
                loadTags();
              }}
              sx={{
                minWidth: 32,
                width: 32,
                height: 32,
                borderRadius: '50%',
                p: 0,
              }}
            >
              +
            </Button>
          </Stack>

          <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {allTags.map((t) => {
              const isEditing = editingTagId === t.tagId;

              return (
                <Box
                  key={t.tagId}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.8,
                    px: 1.5,
                    py: 0.6,
                    borderRadius: 2,
                    bgcolor: t.color,
                    cursor: 'pointer',
                  }}
                  onClick={() => {
                    if (!isEditing) {
                      setEditingTagId(t.tagId);
                      setEditingName(t.name);
                    }
                  }}
                >
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: t.color,
                      border: '2px solid #fff',
                    }}
                  />

                  {isEditing ? (
                    <TextField
                      value={editingName}
                      size="small"
                      variant="standard"
                      autoFocus
                      onChange={(e) => setEditingName(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      onBlur={async () => {
                        if (editingName.trim() && editingName !== t.name) {
                          await fetch(`${API_URL}/tags/${t.tagId}`, {
                            method: 'PUT',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ name: editingName }),
                          });
                          loadTags();
                        }
                        setEditingTagId(null);
                      }}
                      onKeyDown={async (e) => {
                        if (e.key === 'Enter') {
                          if (editingName.trim() && editingName !== t.name) {
                            await fetch(`${API_URL}/tags/${t.tagId}`, {
                              method: 'PUT',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({ name: editingName }),
                            });
                            loadTags();
                          }
                          setEditingTagId(null);
                        }
                      }}
                      sx={{ minWidth: 60, maxWidth: 120 }}
                    />
                  ) : (
                    <Typography variant="caption">{t.name}</Typography>
                  )}

                  <IconButton
                    size="small"
                    onClick={async (e) => {
                      e.stopPropagation();
                      await fetch(`${API_URL}/tags/${t.tagId}`, {
                        method: 'DELETE',
                      });
                      loadTags();
                    }}
                    sx={{ p: 0.3 }}
                  >
                    <DeleteIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Modal>
    </Box>
  );
}
